import { Dispatch, SetStateAction } from 'react'

import { Checkbox } from '../Checkbox'
import { Advice, Category } from './types'

interface Props {
  language: string
  setFilteredAdvices: Dispatch<SetStateAction<Advice[]>>
  allAdvices: Advice[]
  allTags: string[]
  categories: Category[]
  checkedTags: string[]
  setCheckedTags: Dispatch<SetStateAction<string[]>>
  checkedTopics: string[]
  setCheckedTopics: Dispatch<SetStateAction<string[]>>
}

export function Filters(props: Props) {
  const {
    language,
    setFilteredAdvices,
    allAdvices,
    allTags,
    categories,
    checkedTags,
    setCheckedTags,
    checkedTopics,
    setCheckedTopics,
  } = props
  const allTopics: string[][] = [
    ...new Set(
      categories.flatMap((category) =>
        category.topics
          ? category.topics.map((topic) => [topic.title, category.title])
          : [],
      ),
    ),
  ]
  const checkCategoryHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setCheckedTopics([
        ...new Set([
          ...checkedTopics,
          ...allTopics
            .filter((topic) => topic[1] === e.target.name)
            .map((t) => t[0]),
        ]),
      ])
      setFilteredAdvices(
        allAdvices.filter(
          (advice) =>
            advice.topics && checkedTopics.includes(advice.topics[0].title),
        ),
      )
    } else {
      setCheckedTopics([
        ...new Set([
          ...checkedTopics.filter((topic: string) =>
            allTopics
              .filter((t) => t[0] === topic && t[1] !== e.target.name)
              .map((t) => t[0])
              .includes(topic),
          ),
        ]),
      ])
      if (checkedTopics.length === 0 && checkedTags.length === 0) {
        setFilteredAdvices(allAdvices)
      }
    }
  }

  const checkTopicHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setCheckedTopics([...checkedTopics, e.target.name])
      setFilteredAdvices(
        allAdvices.filter(
          (advice) =>
            advice.topics && checkedTopics.includes(advice.topics[0].title),
        ),
      )
    } else {
      setCheckedTopics(checkedTopics.filter((c) => c !== e.target.name))
    }
  }

  const checkTagHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const tag = e.target.name
    if (e.target.checked) {
      setCheckedTags([...checkedTags, tag])
      setFilteredAdvices(
        allAdvices.filter(
          (advice) =>
            advice.tags && advice.tags.map((t) => t.title).includes(tag),
        ),
      )
    } else {
      setCheckedTags([...checkedTags.filter((c) => c !== tag)])
      setFilteredAdvices(
        allAdvices.filter(
          (advice) =>
            advice.tags && advice.tags.map((t) => t.title).includes(tag),
        ),
      )
    }
  }

  function determineCheckedCategory(category: Category) {
    return category.topics
      ? category.topics.some((topic) => checkedTopics.includes(topic.title))
      : false
  }

  const translations = {
    fi: {
      showFilters: 'Tunniste',
      tags: 'Tunniste',
      categoriesAndTopics: 'Kategoriat ja aiheet',
      searchPlaceholder: 'Hae neuvoja',
      all: 'Kaikki',
    },
    nb: {
      showFilters: 'Taggfilter',
      tags: 'Tagger',
      categoriesAndTopics: 'Kategorier og emner',
      searchPlaceholder: 'Søk etter råd',
      all: 'Alle',
    },
    en: {
      showFilters: 'Show tag filters',
      tags: 'Tags',
      categoriesAndTopics: 'Categories and Topics',
      searchPlaceholder: 'Search for advice',
      all: 'All',
    },
  }

  const t =
    translations[language as keyof typeof translations] || translations.en

  return (
    <aside className="sticky flex flex-col gap-5 border-r-blue-100 pr-4 md:w-4/12 md:border-r lg:w-3/12 order-1 md:order-none">
      <input
        placeholder={t.searchPlaceholder}
        type="text"
        className="rounded border border-blue-100 p-2"
        onChange={(e) =>
          setFilteredAdvices(
            allAdvices.filter(
              (article) =>
                article &&
                article.title
                  .toLowerCase()
                  .includes(e.target.value.toLowerCase()),
            ),
          )
        }
      />
      {allTags.length > 0 && (
        <div className="">
          <h3 className="font-semibold">{t.tags}</h3>
          <details>
            <summary className="cursor-pointer">{t.showFilters}</summary>
            <ul className="">
              {allTags.map((tag) => (
                <li key={tag} className="flex gap-2 pb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name={tag}
                      checked={checkedTags.includes(tag)}
                      onChange={checkTagHandler}
                    />
                    <span>{tag}</span>
                  </label>
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}
      <div className="">
        <h3 className="font-semibold">{t.categoriesAndTopics}</h3>
        <ul className="">
          {categories
            //.filter(c => c.slug !== 'policy-landscape')
            .map((category: Category) => (
              <li key={category.title} className="pb-3">
                <details>
                  <summary className="cursor-pointer">{category.title}</summary>
                  <label className="flex items-center gap-2 cursor-pointer py-2 ml-3 pl-2">
                    <span>
                      <Checkbox
                        name={category.title}
                        checked={determineCheckedCategory}
                        onChange={checkCategoryHandler}
                      />
                    </span>
                    <span>{t.all}</span>
                  </label>
                  <ul className="ml-6 pl-4">
                    {category.topics &&
                      category.topics
                        .filter((topic) => topic !== undefined)
                        .map((topic) => ({
                          title: topic.title,
                        }))
                        .map((f: { title: string }) => (
                          <li key={f.title} className="pb-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                name={f.title}
                                checked={checkedTopics.includes(f.title)}
                                onChange={checkTopicHandler}
                              />
                              <span>{f.title}</span>
                            </label>
                          </li>
                        ))}
                  </ul>
                </details>
              </li>
            ))}
        </ul>
      </div>
    </aside>
  )
}
